<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import { Star } from "@lucide/vue";

const props = withDefaults(
  defineProps<{
    isFavorite: boolean;
    animate?: boolean;
  }>(),
  { animate: true }
);
const emits = defineEmits(["toggleFavorite"]);

const isBursting = ref(false);
const internalFavorite = ref(false);

const handleToggleFavorite = () => {
  emits("toggleFavorite");
};

const triggerBurst = () => {
  if (!props.animate) return;
  isBursting.value = false;
  requestAnimationFrame(() => {
    isBursting.value = true;
    setTimeout(() => (isBursting.value = false), 550);
  });
};

// Sync internal state on mount (triggers animation if already favorite)
onMounted(() => {
  if (props.isFavorite) {
    requestAnimationFrame(() => {
      internalFavorite.value = true;
      triggerBurst();
    });
  }
});

// Play animation when toggling → true
watch(
  () => props.isFavorite,
  (newVal, oldVal) => {
    internalFavorite.value = newVal;
    if (newVal && !oldVal) {
      triggerBurst();
    }
  }
);
</script>

<template>
  <div
    class="group rounded-8 duration-fast ease-standard relative flex h-7 w-7 cursor-pointer items-center justify-center transition-all active:scale-95"
    :class="[internalFavorite ? 'hover:bg-app-yellow-100/50' : 'hover:bg-app-yellow-100/60']"
    @click="handleToggleFavorite">
    <!-- keep your existing star + burst exactly -->
    <span
      class="icon-wrap"
      :class="{ burst: isBursting }">
      <Transition
        mode="out-in"
        name="fade-in-out">
        <Star
          v-if="internalFavorite"
          key="on"
          class="star icon-md text-app-yellow-400 drop-shadow-[0_0_2px_rgba(250,204,21,0.4)] group-hover:drop-shadow-[0_0_8px_rgba(250,204,21,1)]"
          :stroke-width="1.5"
          fill="currentColor" />
        <Star
          v-else
          key="off"
          class="star icon-md text-app-yellow-400 opacity-95"
          :stroke-width="1.5" />
      </Transition>

      <!-- Celebration particles -->

      <span class="p p1" />
      <span class="p p2" />
      <span class="p p3" />
      <span class="p p4" />
      <span class="p p5" />
      <span class="p p6" />
    </span>
  </div>
</template>

<style scoped>
.icon-wrap {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 32px;
  height: 32px;
  color: #facc15; /* base star gold */
}

/* STAR POP */
.icon-wrap.burst .star {
  animation: pop 520ms cubic-bezier(0.2, 0.9, 0.2, 1);
  filter: drop-shadow(0 0 6px rgba(250, 204, 21, 0.9));
}

@keyframes pop {
  0% {
    transform: scale(1);
  }
  20% {
    transform: scale(1.35) rotate(-8deg);
  }
  45% {
    transform: scale(0.95) rotate(4deg);
  }
  100% {
    transform: scale(1) rotate(0);
  }
}

/* RING */
.ring {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 2px solid currentColor;
  border-radius: 999px;
  opacity: 0;
  pointer-events: none;
  border-color: #fde047; /* yellow-300 */
  box-shadow: var(--shadow-glow-yellow); /* migrated to token */
}

.icon-wrap.burst .ring {
  animation: ring 520ms ease-out;
}

@keyframes ring {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  15% {
    opacity: 0.5;
  }
  100% {
    transform: scale(2.4);
    opacity: 0;
  }
}

/* PARTICLES */
.p {
  position: absolute;
  width: 6px;
  height: 6px;
  background: linear-gradient(180deg, #fde047, #f59e0b); /* yellow → amber */
  border-radius: 999px;
  opacity: 0;
  pointer-events: none;
}

.icon-wrap.burst .p {
  animation: fly 520ms ease-out;
}

.p1 {
  --dx: 0px;
  --dy: -18px;
}
.p2 {
  --dx: 14px;
  --dy: -10px;
}
.p3 {
  --dx: 18px;
  --dy: 2px;
}
.p4 {
  --dx: 0px;
  --dy: 18px;
}
.p5 {
  --dx: -14px;
  --dy: -10px;
}
.p6 {
  --dx: -18px;
  --dy: 2px;
}

@keyframes fly {
  0% {
    transform: translate(0, 0) scale(0.2);
    opacity: 0;
  }
  15% {
    opacity: 0.85;
    transform: translate(0, 0) scale(1);
  }
  100% {
    transform: translate(var(--dx), var(--dy)) scale(0.2);
    opacity: 0;
  }
}

/* Respect reduced motion */
@media (prefers-reduced-motion: reduce) {
  .icon-wrap.burst * {
    animation: none !important;
  }
}

.icon-wrap {
  /* unchanged, but allow particles to extend */
  overflow: visible;
  z-index: 1;
}

/* Make the active state glow a bit more (optional, subtle) */
.star {
  transition:
    filter 200ms ease,
    transform 200ms ease;
}

.group:hover .star {
  transform: scale(1.25);
}
</style>
